<template>
  <div>
    <div v-if="!activeMode">
      <p class="text-lg font-bold leading-tight text-heading">
        {{ $t('facebook_share_heading') }}
      </p>
      <p class="mt-1 text-supplement">
        {{ $t('facebook_share_subheading') }}
      </p>

      <div class="mt-5 space-y-3">
        <button
          v-for="mode in modes"
          :key="mode.value"
          type="button"
          class="group flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors sm:gap-4 sm:p-5"
          :class="mode.classes.card"
          @click="selectMode(mode.value)"
        >
          <span
            class="grid size-9 shrink-0 place-items-center self-start rounded-full sm:size-11 sm:text-lg"
            :class="mode.classes.icon"
          >
            <Icon
              :icon="mode.icon"
              prefix="far"
            />
          </span>
          <span class="min-w-0 grow break-words">
            <strong class="block text-heading">{{ mode.title }}</strong>
            <span class="mt-0.5 block text-sm">{{ mode.description }}</span>
            <ul class="mt-3 space-y-1 text-sm">
              <li
                v-for="feature in mode.features"
                :key="feature"
                class="flex items-baseline gap-2"
              >
                <Icon
                  icon="check"
                  class="shrink-0"
                  :class="mode.classes.accent"
                />
                <span class="min-w-0">{{ feature }}</span>
              </li>
            </ul>
          </span>
          <Icon
            icon="chevron-right"
            class="shrink-0 text-supplement transition-transform group-hover:translate-x-1"
          />
        </button>
      </div>
    </div>

    <div v-else>
      <div
        v-if="!fixedMode"
        class="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1 text-sm"
      >
        <button
          v-for="mode in modes"
          :key="mode.value"
          type="button"
          class="flex min-w-0 items-center justify-center gap-2 rounded-md px-3 py-2.5 text-center font-semibold leading-tight transition-colors"
          :class="activeMode === mode.value ? ['bg-white shadow', mode.classes.accent] : 'text-supplement hover:text-paragraph'"
          @click="selectMode(mode.value)"
        >
          <Icon
            :icon="mode.icon"
            prefix="far"
            class="shrink-0"
          />
          <span class="min-w-0 break-words">{{ mode.title }}</span>
        </button>
      </div>
      <p
        v-if="!fixedMode"
        class="mb-8 mt-3 text-sm text-supplement"
      >
        {{ modes.find(mode => mode.value === activeMode)?.description }}
      </p>

      <template
        v-for="mode in modes"
        :key="mode.value"
      >
        <div
          v-if="loadedModes.includes(mode.value)"
          v-show="activeMode === mode.value"
        >
          <slot :name="mode.value" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ShareMode } from '~/types/share'

const props = defineProps<{
  modes: ShareMode[]
  fixedMode?: string
}>()

// No mode is chosen until the user picks one; each mode's content is mounted on first
// selection and then kept alive so switching back and forth preserves generated drafts.
const activeMode = ref<string | null>(props.fixedMode ?? null)
const loadedModes = ref<string[]>(props.fixedMode ? [props.fixedMode] : [])

const selectMode = (mode: string) => {
  activeMode.value = mode

  if (!loadedModes.value.includes(mode)) {
    loadedModes.value.push(mode)
  }
}
</script>
